import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Hook for managing scroll behavior and BackToTop button visibility
 * @param {number} threshold - Scroll threshold in pixels to show the BackToTop button (default: 300)
 * @returns {Object} - scrollContainerRef, showBackToTop state, and scrollToTop function
 */
export const useScrollManagement = (threshold = 300) => {
  const [showBackToTop, setShowBackToTop] = useState(false);
  const scrollContainerRef = useRef(null);

  // Setup scroll listener for BackToTop button
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const scrollTop = container.scrollTop;
      setShowBackToTop(scrollTop > threshold);
    };

    // Add scroll listener with passive option for better performance
    container.addEventListener('scroll', handleScroll, { passive: true });

    // Initial check
    handleScroll();

    return () => container.removeEventListener('scroll', handleScroll);
  }, [threshold]);

  // Scroll to top function with smooth behavior
  const scrollToTop = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }, []);

  return {
    scrollContainerRef,
    showBackToTop,
    scrollToTop,
  };
};

export default useScrollManagement;
